import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Lock, RotateCw, ArrowLeft, ArrowRight, LayoutDashboard,
    Users, GitBranch, BarChart3, MessagesSquare, MapPin,
} from "lucide-react";
import { Container, Reveal, SectionHeader } from "./common";
import { AppChrome, DashboardContent } from "./DashboardMockup";
import LeadsScreen from "./LeadsScreen";
import PipelineScreen from "./PipelineScreen";
import EngageScreen from "./EngageScreen";
import FieldSalesScreen from "./FieldSalesScreen";
import ReportsScreen from "./ReportsScreen";

const SCREENS = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/home", blurb: "Your AI-powered sales command center." },
    { id: "leads", label: "Leads", icon: Users, path: "/leads", blurb: "Every lead scored, assigned and followed up." },
    { id: "pipeline", label: "Pipeline", icon: GitBranch, path: "/opportunities", blurb: "Drag deals from first touch to won." },
    { id: "engage", label: "Engage", icon: MessagesSquare, path: "/engage", blurb: "Campaigns and sequences that convert." },
    { id: "field", label: "Field Sales", icon: MapPin, path: "/field-sales", blurb: "Routes, check-ins and visits — live." },
    { id: "reports", label: "Reports", icon: BarChart3, path: "/reports", blurb: "Decision-ready insight, always current." },
];

const ProductTour = () => {
    const [active, setActive] = useState("dashboard");
    const screen = SCREENS.find((s) => s.id === active);

    return (
        <section id="product-tour" data-testid="product-tour-section" className="bg-white py-20 lg:py-28">
            <Container>
                <SectionHeader
                    eyebrow="Product Tour"
                    title="Take a look inside Kryvo CRM"
                    sub="Real product screens, recreated from the live app — switch between the workspaces your team will use every day."
                />

                <Reveal delay={0.1}>
                    <div className="mt-10 flex flex-wrap justify-center gap-2">
                        {SCREENS.map((s) => (
                            <button
                                key={s.id}
                                data-testid={`tour-tab-${s.id}`}
                                onClick={() => setActive(s.id)}
                                aria-pressed={active === s.id}
                                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                                    active === s.id
                                        ? "bg-[#16A34A] text-white shadow-lg shadow-[#16A34A]/25"
                                        : "border border-[#E5E7EB] bg-white text-[#475569] hover:border-[#16A34A]/40 hover:text-[#15803D]"
                                }`}
                            >
                                <s.icon className="h-4 w-4" />
                                {s.label}
                            </button>
                        ))}
                    </div>
                    <p className="mt-4 text-center text-sm font-medium text-[#64748B]">{screen.blurb}</p>
                </Reveal>

                <Reveal delay={0.15}>
                    {/* Browser frame */}
                    <div
                        data-testid="tour-browser-frame"
                        className="mx-auto mt-8 max-w-5xl overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-2xl shadow-slate-900/10"
                    >
                        <div className="flex items-center gap-3 border-b border-[#EDF0F3] bg-[#F8FAFC] px-4 py-2.5">
                            <span className="flex gap-1.5">
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FCA5A5]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FCD34D]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#86EFAC]" />
                            </span>
                            <ArrowLeft className="hidden h-3.5 w-3.5 text-[#CBD5E1] sm:block" />
                            <ArrowRight className="hidden h-3.5 w-3.5 text-[#CBD5E1] sm:block" />
                            <RotateCw className="hidden h-3 w-3 text-[#CBD5E1] sm:block" />
                            <span className="mx-auto flex w-full max-w-xs items-center justify-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-[10px] font-semibold text-[#475569]">
                                <Lock className="h-2.5 w-2.5 text-[#16A34A]" />
                                admin.kryvocrm.com{screen.path}
                            </span>
                            <span className="hidden w-16 sm:block" />
                        </div>

                        <div className="relative">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={active}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {active === "dashboard" && (
                                        <div>
                                            <AppChrome active="Home" />
                                            <DashboardContent />
                                        </div>
                                    )}
                                    {active === "leads" && <LeadsScreen />}
                                    {active === "pipeline" && <PipelineScreen />}
                                    {active === "engage" && <EngageScreen />}
                                    {active === "field" && <FieldSalesScreen />}
                                    {active === "reports" && <ReportsScreen />}
                                </motion.div>
                            </AnimatePresence>
                            {/* clipped-frame fade */}
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent" />
                        </div>
                    </div>
                    <p className="mt-4 text-center text-xs text-[#94A3B8]">
                        Product UI shown with demo data for illustration.
                    </p>
                </Reveal>
            </Container>
        </section>
    );
};

export default ProductTour;
